import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { ListGangsResponse } from '../../api/gangsApi';

type GangMember = ListGangsResponse['gangs'][number]['members'][number];

interface GangMemberListItemProps {
  member: GangMember;
  itemPrefix: string;
  avatarColor: (theme: any) => { bgcolor: string; color: string };
  formatCurrency: (amount: number) => string;
}

const getInitials = (
  givenName?: string | null,
  familyName?: string | null
): string => {
  const first = (givenName ?? '').trim().charAt(0);
  const last = (familyName ?? '').trim().charAt(0);
  return first || last ? `${first}${last}`.toUpperCase() : 'U';
};

const getFullName = (member: GangMember): string => {
  const given = (member.givenName ?? '').trim();
  const family = (member.familyName ?? '').trim();
  return given || family ? `${given} ${family}`.trim() : 'Unknown';
};

export const GangMemberListItem = ({
  member,
  itemPrefix,
  avatarColor,
  formatCurrency,
}: GangMemberListItemProps) => {
  const {
    defaultSplitPercentage,
    defaultRate,
    defaultUnitsValue,
    canSeeSplit,
  } = member;
  const hasSplit =
    defaultSplitPercentage !== null && defaultSplitPercentage !== undefined;
  const hasFixedRate = defaultRate !== null && defaultRate !== undefined;

  return (
    <ListItem
      sx={{ pl: 0 }}
      data-testid={`${itemPrefix}-item-${member.userId}`}
    >
      <ListItemAvatar>
        <Avatar
          sx={theme => ({
            width: 32,
            height: 32,
            ...avatarColor(theme),
          })}
          data-testid={`${itemPrefix}-avatar-${member.userId}`}
        >
          {getInitials(member.givenName, member.familyName)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            variant="subtitle2"
            fontWeight={600}
            component="span"
            data-testid={`${itemPrefix}-name-${member.userId}`}
          >
            {getFullName(member)}
          </Typography>
        }
        secondary={
          <Box component="span" sx={{ display: 'block' }}>
            {canSeeSplit !== null && canSeeSplit !== undefined && (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
                data-testid={`${itemPrefix}-visibility-${member.userId}`}
                data-can-see-split={canSeeSplit ?? undefined}
              >
                {canSeeSplit ? (
                  <>
                    <VisibilityIcon fontSize="small" color="success" />
                    <Typography
                      variant="caption"
                      color="success.main"
                      component="span"
                    >
                      Can see splits
                    </Typography>
                  </>
                ) : (
                  <>
                    <VisibilityOffIcon fontSize="small" color="error" />
                    <Typography
                      variant="caption"
                      color="error.main"
                      component="span"
                    >
                      Cannot see splits
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {hasSplit || hasFixedRate ? (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mt: 0.5,
                }}
                data-testid={`${itemPrefix}-financial-${member.userId}`}
              >
                {hasSplit && (
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                    data-testid={`${itemPrefix}-split-${member.userId}`}
                    data-percentage={
                      defaultSplitPercentage !== null &&
                      defaultSplitPercentage !== undefined
                        ? Math.round(defaultSplitPercentage * 1000) / 10
                        : undefined
                    }
                  >
                    <PercentIcon fontSize="small" color="primary" />
                    <Typography
                      variant="caption"
                      color="primary.main"
                      component="span"
                    >
                      {Math.round(defaultSplitPercentage! * 1000) / 10}% split
                    </Typography>
                  </Box>
                )}

                {hasFixedRate && (
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                    data-testid={`${itemPrefix}-fixedrate-${member.userId}`}
                    data-fixed-rate={defaultRate ?? undefined}
                    data-units-value={defaultUnitsValue ?? undefined}
                  >
                    <MoneyIcon fontSize="small" color="secondary" />
                    <Typography
                      variant="caption"
                      color="secondary.main"
                      component="span"
                    >
                      {formatCurrency(defaultRate!)}
                      {defaultUnitsValue !== null &&
                      defaultUnitsValue !== undefined
                        ? ` × ${defaultUnitsValue} units`
                        : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>
        }
      />
    </ListItem>
  );
};
